import React, { useState, useEffect } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { API_BASE } from '../config';
import { useDispatch, useSelector } from 'react-redux';
import { loginSuccess } from '../slices/authSlice';
import './Login.css';

const PORTAL_CONFIG = {
  ops: {
    verifyPath: '/api/portal/ops/verify-otp',
    loginPath: '/ops/login',
    redirectPrefix: '/ops',
    defaultRedirect: '/ops',
    title: 'Ops sign in',
  },
  doctor: {
    verifyPath: '/api/portal/doctor/verify-otp',
    loginPath: '/doctor/login',
    redirectPrefix: '/doctor',
    defaultRedirect: '/doctor/pending',
    title: 'Doctor sign in',
  },
};

function portalKindFromPath(pathname) {
  const path = String(pathname || '');
  if (path.startsWith('/ops/login/otp')) return 'ops';
  if (path.startsWith('/doctor/login/otp')) return 'doctor';
  return null;
}

function resolvePortalKind(location) {
  const fromPath = portalKindFromPath(location.pathname);
  if (fromPath) {
    try {
      sessionStorage.setItem('portal_auth_kind', fromPath);
    } catch {
      // ignore storage errors
    }
    return fromPath;
  }
  const fromState = location.state?.portalKind;
  if (fromState && PORTAL_CONFIG[fromState]) return fromState;
  const stored = sessionStorage.getItem('portal_auth_kind');
  return PORTAL_CONFIG[stored] ? stored : null;
}

export default function PortalOtp() {
  const navigate = useNavigate();
  const location = useLocation();
  const [otp, setOtp] = useState('');
  const [error, setError] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const dispatch = useDispatch();
  const token = useSelector((state) => state.auth.token);
  const authKind = useSelector((state) => state.auth.authKind);

  const portalKind = resolvePortalKind(location);
  const email = sessionStorage.getItem('portal_auth_email');
  const config = portalKind ? PORTAL_CONFIG[portalKind] : null;

  useEffect(() => {
    if (!config) {
      navigate('/login', { replace: true });
      return;
    }
    if (token && authKind === portalKind) {
      const from = location.state?.from;
      navigate(
        from && from.startsWith(config.redirectPrefix) ? from : config.defaultRedirect,
        { replace: true }
      );
      return;
    }
    if (!email) {
      navigate(config.loginPath, { replace: true });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token, authKind, portalKind, config, email]);

  const handleVerify = async (e) => {
    e.preventDefault();

    if (!config || !portalKind) return;

    if (otp.length !== 6) {
      setError('Enter valid 6-digit OTP');
      return;
    }

    setIsSubmitting(true);
    setError('');

    try {
      const res = await fetch(`${API_BASE}${config.verifyPath}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email, otp }),
      });

      const data = await res.json();

      if (!res.ok) {
        setError(data.error || 'Invalid OTP');
        return;
      }

      dispatch(
        loginSuccess({
          token: data.token,
          user: data.profile || { email },
          roles: data.roles || {
            isOps: portalKind === 'ops',
            isDoctor: portalKind === 'doctor',
          },
          authKind: portalKind,
        })
      );

      sessionStorage.removeItem('portal_auth_email');
      sessionStorage.removeItem('portal_auth_kind');

      const from = location.state?.from;
      const target =
        from && from.startsWith(config.redirectPrefix) ? from : config.defaultRedirect;
      setTimeout(() => navigate(target, { replace: true }), 50);
    } catch (err) {
      setError('Server error');
      console.error(err);
    } finally {
      setIsSubmitting(false);
    }
  };

  if (!config) return null;

  return (
    <div className="proceed-page">
      <div className="proceed-bg-shapes" aria-hidden="true">
        <span className="proceed-shape proceed-shape-1" />
        <span className="proceed-shape proceed-shape-2" />
      </div>

      <Link to="/" className="proceed-logo">
        <img src="/images/unaffectedLogo.png" alt="Unaffected Healthcare Logo" className="proceed-logo-image" />
        <span className="proceed-logo-text">Unaffected Healthcare</span>
      </Link>

      <div className="login-page">
        <div className="login-card-wrap">
          <div className="login-card">
            <h1 className="login-title login-title-form">Enter OTP</h1>
            <p className="login-subtitle">
              {config.title} — OTP sent to {email}
            </p>

            <form onSubmit={handleVerify} className="login-form">
              <div className="form-group">
                <input
                  type="text"
                  value={otp}
                  onChange={(e) => setOtp(e.target.value.replace(/\D/g, '').slice(0, 6))}
                  placeholder="Enter 6-digit OTP"
                  maxLength={6}
                  className="login-input"
                />
              </div>
              {error && <p className="login-inline-error">{error}</p>}

              <button type="submit" className="login-btn-submit" disabled={isSubmitting}>
                {isSubmitting ? 'Verifying...' : 'Verify OTP'}
              </button>
            </form>

            <button
              type="button"
              className="login-link-back"
              style={{ marginTop: '1rem', background: 'none', border: 'none', cursor: 'pointer' }}
              onClick={() => navigate(config.loginPath)}
            >
              Back to sign in
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
